// /audit — query sobre audit_log con filtros.
//
// Filtros: action (exact), actor_type (enum), actor_id, target_type,
// target_id, from/to (datetime local → ISO), limit.

import { FormEvent, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAuditLog, type AuditQuery } from '@/lib/api';
import { fieldCls } from '@/lib/form-styles';

function toIsoOrUndefined(dtLocal: string): string | undefined {
  if (!dtLocal) return undefined;
  const d = new Date(dtLocal);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export default function AuditLog(): JSX.Element {
  const [filters, setFilters] = useState<AuditQuery>({});
  const [form, setForm] = useState({
    action: '',
    actor_type: '',
    actor_id: '',
    target_type: '',
    target_id: '',
    from: '',
    to: '',
    limit: '100',
  });

  const q = useQuery({
    queryKey: ['admin-audit', filters],
    queryFn: () => fetchAuditLog(filters),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const next: AuditQuery = {};
    if (form.action) next.action = form.action;
    if (form.actor_type) {
      next.actor_type = form.actor_type as Exclude<AuditQuery['actor_type'], undefined>;
    }
    if (form.actor_id) next.actor_id = form.actor_id;
    if (form.target_type) next.target_type = form.target_type;
    if (form.target_id) next.target_id = form.target_id;
    const fromIso = toIsoOrUndefined(form.from);
    if (fromIso) next.from = fromIso;
    const toIso = toIsoOrUndefined(form.to);
    if (toIso) next.to = toIso;
    if (form.limit) next.limit = parseInt(form.limit, 10);
    setFilters(next);
  }

  function onReset(): void {
    setForm({
      action: '',
      actor_type: '',
      actor_id: '',
      target_type: '',
      target_id: '',
      from: '',
      to: '',
      limit: '100',
    });
    setFilters({});
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 data-help-page="audit.page" data-help="audit.page" className="text-2xl font-semibold">Audit log</h1>
        <p className="text-sm text-ink-muted">
          Eventos sensibles (logins, cambios, acciones admin). Ordenado por fecha desc.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        data-help="audit.filters"
        className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
      >
        <label className="text-sm space-y-1">
          <span className="font-medium">Action</span>
          <input
            type="text"
            placeholder="admin.login.success"
            value={form.action}
            onChange={(e) => setForm({ ...form, action: e.target.value })}
            className={fieldCls()}
          />
        </label>
        <label className="text-sm space-y-1">
          <span className="font-medium">Actor type</span>
          <select
            value={form.actor_type}
            onChange={(e) => setForm({ ...form, actor_type: e.target.value })}
            className={fieldCls()}
          >
            <option value="">cualquiera</option>
            <option value="admin_user">admin_user</option>
            <option value="user">user</option>
            <option value="partner">partner</option>
            <option value="system">system</option>
            <option value="self">self</option>
          </select>
        </label>
        <label className="text-sm space-y-1">
          <span className="font-medium">Target type</span>
          <input
            type="text"
            placeholder="user / partner / ..."
            value={form.target_type}
            onChange={(e) => setForm({ ...form, target_type: e.target.value })}
            className={fieldCls()}
          />
        </label>
        <label className="text-sm space-y-1">
          <span className="font-medium">Target id</span>
          <input
            type="text"
            value={form.target_id}
            onChange={(e) => setForm({ ...form, target_id: e.target.value })}
            className={fieldCls({ mono: true })}
          />
        </label>
        <label className="text-sm space-y-1">
          <span className="font-medium">Desde</span>
          <input
            type="datetime-local"
            value={form.from}
            onChange={(e) => setForm({ ...form, from: e.target.value })}
            className={fieldCls()}
          />
        </label>
        <label className="text-sm space-y-1">
          <span className="font-medium">Hasta</span>
          <input
            type="datetime-local"
            value={form.to}
            onChange={(e) => setForm({ ...form, to: e.target.value })}
            className={fieldCls()}
          />
        </label>
        <label className="text-sm space-y-1">
          <span className="font-medium">Límite (1-500)</span>
          <input
            type="number"
            min={1}
            max={500}
            value={form.limit}
            onChange={(e) => setForm({ ...form, limit: e.target.value })}
            className={fieldCls()}
          />
        </label>
        <div className="flex items-end gap-2">
          <button
            type="submit"
            className="flex-1 min-h-[2.5rem] rounded bg-brand hover:bg-brand-hover text-white font-medium px-3"
          >
            Aplicar
          </button>
          <button
            type="button"
            onClick={onReset}
            className="min-h-[2.5rem] rounded border border-surface-border dark:border-surface-dark-border px-3 text-sm"
          >
            Reset
          </button>
        </div>
      </form>

      <section className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border">
        <header className="px-5 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center">
          <h2 className="text-sm font-medium flex-1">
            Resultados {q.data ? `(${q.data.count})` : ''}
          </h2>
          {q.isFetching ? <span className="text-xs text-ink-muted">Actualizando…</span> : null}
        </header>
        <div className="overflow-x-auto">
          {q.data?.entries.length === 0 ? (
            <p className="text-sm text-ink-muted p-5">Sin resultados.</p>
          ) : (
            <table data-help="audit.results" className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-ink-muted border-b border-surface-border dark:border-surface-dark-border">
                  <th className="text-left px-5 py-2">Fecha</th>
                  <th className="text-left px-5 py-2">Action</th>
                  <th className="text-left px-5 py-2">Actor</th>
                  <th className="text-left px-5 py-2">Target</th>
                  <th className="text-left px-5 py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {q.data?.entries.map((a) => (
                  <tr
                    key={a.audit_id}
                    className="border-b border-surface-border dark:border-surface-dark-border last:border-0"
                  >
                    <td className="px-5 py-2 tabular-nums text-ink-muted whitespace-nowrap">
                      {new Date(a.occurred_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-2 font-mono text-xs">{a.action}</td>
                    <td className="px-5 py-2 text-xs">
                      <div>{a.actor_type}</div>
                      <div className="text-ink-muted font-mono">{a.actor_id}</div>
                    </td>
                    <td className="px-5 py-2 text-xs">
                      {a.target_type ? <div>{a.target_type}</div> : null}
                      {a.target_id ? <div className="text-ink-muted font-mono break-all">{a.target_id}</div> : null}
                    </td>
                    <td className="px-5 py-2 text-xs text-ink-muted">{a.reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
